/**
 * Account Sharing Modal
 * 
 * Allows account owners to invite other users to access their portfolio
 * with configurable permission levels (read_only or normal_user).
 */

import React, { useState, useEffect } from 'react';
import { sharedAccessService } from '../../services/SharedAccessService';
import { useScrollLock } from '../../hooks/useScrollLock';

// Type imported from service
import type { SharedAccessLevel } from '../../services/SharedAccessService';

interface SharedUser {
  id: string;
  email: string;
  accessLevel: SharedAccessLevel;
  status: 'pending' | 'accepted';
  invitedAt?: string;
  acceptedAt?: string;
  expiresAt?: string;
}

interface AccountSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AccountSharingModal: React.FC<AccountSharingModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Prevent background scrolling when modal is open
  useScrollLock(isOpen);

  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState<SharedAccessLevel>('read_only');
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load shared users on mount
  useEffect(() => {
    if (isOpen) {
      loadSharedUsers();
    }
  }, [isOpen]);

  const loadSharedUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const users = await sharedAccessService.getSharedUsers();
      setSharedUsers(users);
    } catch (err: any) {
      setError(err.message || 'Failed to load shared users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setInviting(true);
      setError(null);
      setSuccess(null);

      await sharedAccessService.inviteUser(email.trim(), accessLevel);

      setSuccess(`Invitation sent to ${email}`);
      setEmail('');

      // Reload the list
      await loadSharedUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateAccessLevel = async (inviteId: string, newLevel: SharedAccessLevel) => {
    try {
      setError(null);
      await sharedAccessService.updateAccessLevel(inviteId, newLevel);

      // Update local state
      setSharedUsers(users =>
        users.map(user =>
          user.id === inviteId ? { ...user, accessLevel: newLevel } : user
        )
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update access level');
    }
  };

  const handleRevoke = async (inviteId: string, email: string) => {
    if (!confirm(`Are you sure you want to revoke access for ${email}?`)) {
      return;
    }

    try {
      setError(null);
      await sharedAccessService.revokeAccess(inviteId);

      // Remove from local state
      setSharedUsers(users => users.filter(user => user.id !== inviteId));
    } catch (err: any) {
      setError(err.message || 'Failed to revoke access');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Share Portfolio Access
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Invite Form */}
          <form onSubmit={handleInvite} className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Invite Someone
            </h3>
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={inviting}
              />
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value as SharedAccessLevel)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={inviting}
              >
                <option value="read_only">Read Only</option>
                <option value="normal_user">Can Edit</option>
              </select>
              <button
                type="submit"
                disabled={inviting || !email.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {inviting ? 'Sending...' : 'Invite'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Read Only:</strong> Can view your portfolio but cannot make changes.
              <br />
              <strong>Can Edit:</strong> Can view and edit properties but cannot invite others.
            </p>
          </form>

          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 
                            rounded-md text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 
                            rounded-md text-green-700 dark:text-green-400 text-sm">
              {success}
            </div>
          )}

          {/* Shared Users List */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              People with Access
            </h3>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
              </div>
            ) : sharedUsers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No one has access to your portfolio yet.</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Invite a financial advisor or family member above.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sharedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center">
                        <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {user.email}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {user.status === 'pending' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              Active
                            </span>
                          )}
                          <span>•</span>
                          <span>Invited {formatDate(user.invitedAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={user.accessLevel}
                        onChange={(e) => handleUpdateAccessLevel(user.id, e.target.value as SharedAccessLevel)}
                        className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded
                                   bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        <option value="read_only">Read Only</option>
                        <option value="normal_user">Can Edit</option>
                      </select>
                      <button
                        onClick={() => handleRevoke(user.id, user.email)}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Revoke access"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSharingModal;
